import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { responseHandler } from '../utils';
import { Envelope } from './types';

/**
 * Dane o NASZYCH prowizjach — dashboard HotelSpot.
 *
 * Źródłem jest Stripe (przez nasz backend za `verifyAdminAccess`), nie rejestr
 * w Firestore: rejestr powstaje z webhooków, więc jedna nieudana dostawa zostawiłaby
 * w nim dziurę, a to są pieniądze.
 */

export const PLATFORM_FEES_QUERY_KEY = 'platform-stripe-fees';
export const PLATFORM_SUMMARY_QUERY_KEY = 'platform-stripe-summary';

export interface FeeRow {
  feeId: string;
  created: number;
  accountId: string;
  currency: string;
  feeAmount: number;
  feeRefunded: number;
  grossAmount: number;
  grossRefunded: number;
  hotelId: string | null;
  hotelName: string | null;
  outletId: string | null;
  outletName: string | null;
  kind: string | null;
  orderId: string | null;
}

export interface FeesPage {
  rows: FeeRow[];
  hasMore: boolean;
  nextCursor: string | null;
}

export interface CurrencyTotals {
  currency: string;
  fee: number;
  feeRefunded: number;
  feeNet: number;
  gross: number;
  count: number;
}

export interface PlatformSummary {
  balance: {
    available: Array<{ currency: string; amount: number }>;
    pending: Array<{ currency: string; amount: number }>;
  };
  totals: CurrencyTotals[];
  /** Okres ma więcej prowizji, niż backend zdążył zsumować — liczby są niepełne. */
  truncated: boolean;
  livemode: boolean;
}

interface Range {
  createdGte?: number;
  createdLte?: number;
}

export const useGetPlatformFees = (
  range: Range,
  cursor: string | null,
  limit = 25,
): UseQueryResult<FeesPage, AxiosError> => useQuery<FeesPage, AxiosError>({
  // Kursor jest częścią klucza — inaczej react-query oddałby stronę poprzednią z cache.
  queryKey: [PLATFORM_FEES_QUERY_KEY, range.createdGte, range.createdLte, cursor, limit],
  retry: 0,
  queryFn: async () => {
    const response = await axiosInstance.get<Envelope<FeesPage>>('/v3-getPlatformFees', {
      params: { limit, ...range, ...(cursor ? { startingAfter: cursor } : {}) },
    });
    return responseHandler(response).data;
  },
});

export const useGetPlatformStripeSummary = (
  range: Range,
): UseQueryResult<PlatformSummary, AxiosError> => useQuery<PlatformSummary, AxiosError>({
  queryKey: [PLATFORM_SUMMARY_QUERY_KEY, range.createdGte, range.createdLte],
  retry: 0,
  queryFn: async () => {
    const response = await axiosInstance.get<Envelope<PlatformSummary>>('/v3-getPlatformStripeSummary', {
      params: range,
    });
    return responseHandler(response).data;
  },
});
